/**
 * Rebuy recommendations — API-only integration.
 *
 * No rebuy.js, no Rebuy widgets, no Rebuy app blocks. The theme asks Rebuy's
 * public REST API (https://rebuyengine.com/api/v1/…, public key from the
 * "Rebuy public API key" theme setting) for a ranked list of product handles
 * and renders every card through Shopify itself, so prices, badges, wishlist
 * and quick-add are the theme's own and always in the active market/currency
 * (Rebuy's payload only carries base-currency prices).
 *
 *  <rebuy-recommendations>       PDP "You may also like" — full product cards
 *                                (/products/<handle>?view=card) inside the
 *                                theme's card-product-slider (Swiper).
 *  <rebuy-cart-recommendations>  cart-drawer upsell — compact items
 *                                (/products/<handle>?view=upsell) inside the
 *                                theme's native <product-recommendations>.
 *
 * Attribution: each host element carries data-rebuy-attribution /
 * data-rebuy-widget-id; ProductCard (global.js) copies them into the
 * _source / _attribution / _widget_id line-item properties Rebuy's reporting
 * keys off, and the quick-cart drawer inherits them while it was opened from
 * one of these elements (see markQuickCartOrigin).
 *
 * Failure mode: a Rebuy error / empty answer falls back to Shopify's native
 * recommendations (data-url) on the PDP, and to nothing in the drawer.
 */
(function () {
  "use strict";

  const API_BASE = "https://rebuyengine.com/api/v1";
  const CACHE_PREFIX = "rebuy-recs-v2:";
  const CACHE_TTL_MS = 10 * 60 * 1000;
  const FETCH_TIMEOUT_MS = 6000;

  function storage(kind) {
    try {
      return window[kind];
    } catch (e) {
      return null;
    }
  }

  function readCache(key) {
    const s = storage("sessionStorage");
    if (!s) return null;
    try {
      const raw = s.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.exp < Date.now()) {
        s.removeItem(CACHE_PREFIX + key);
        return null;
      }
      return parsed.value;
    } catch (e) {
      return null;
    }
  }

  function writeCache(key, value) {
    const s = storage("sessionStorage");
    if (!s) return;
    try {
      s.setItem(CACHE_PREFIX + key, JSON.stringify({ exp: Date.now() + CACHE_TTL_MS, value }));
    } catch (e) {
      /* quota / private mode — caching is best effort */
    }
  }

  // No visitor identifier is sent to Rebuy (its optional "uuid" would be a
  // persistent third-party id set without consent); requests are anonymous.

  /** fetch() with a timeout; rejects on abort like any network error. */
  function fetchWithTimeout(url, init) {
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS) : null;
    const options = Object.assign({}, init || {}, controller ? { signal: controller.signal } : {});
    return fetch(url, options).finally(() => {
      if (timer) clearTimeout(timer);
    });
  }

  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  /**
   * Ask Rebuy for recommendations. Returns an ordered array of product
   * handles (never throws — [] on any failure).
   *  key        public API key
   *  sourceId   custom data source id (optional → /products/recommended)
   *  productIds seed Shopify product ids (PDP product / cart products)
   *  limit      how many handles to return
   *  excludeIds product ids never to return (e.g. what's already in the cart)
   */
  async function fetchRebuyHandles({ key, sourceId, productIds, limit, excludeIds }) {
    if (!key) return [];
    const seeds = (productIds || []).filter(Boolean);
    const exclude = new Set((excludeIds || []).map(String));
    const path = sourceId ? `/custom/id/${encodeURIComponent(sourceId)}` : "/products/recommended";
    const cacheKey = `${path}|${seeds.join(",")}|${limit}`;
    const cached = readCache(cacheKey);
    if (cached) return cached.filter(h => !exclude.has(h.id)).map(h => h.handle);

    const params = new URLSearchParams();
    params.set("key", key);
    // ask for a few extra so client-side exclusions still leave `limit` items
    params.set("limit", String(Math.min(limit + exclude.size, 50)));
    params.set("filter_oos", "yes");
    if (seeds.length) params.set("shopify_product_ids", seeds.join(","));
    // NOTE: no country_code — prices are never taken from this payload.

    try {
      const response = await fetchWithTimeout(`${API_BASE}${path}?${params.toString()}`, {
        headers: { Accept: "application/json" }
      });
      if (!response.ok) return [];
      const json = await response.json();
      const data = Array.isArray(json && json.data) ? json.data : [];
      const list = data
        .filter(p => p && p.handle && p.id != null)
        .map(p => ({ id: String(p.id), handle: String(p.handle) }));
      writeCache(cacheKey, list);
      return list.filter(h => !exclude.has(h.id)).map(h => h.handle);
    } catch (e) {
      return [];
    }
  }

  /**
   * Fetch one Shopify-rendered fragment for a product handle ("" on failure).
   * `scope` (from the host's data-cache-key: language, country, market,
   * currency, customer) namespaces the cache so a locale/market/login change
   * never serves a fragment rendered for another context.
   */
  async function fetchProductView(handle, view, scope) {
    const cacheKey = `view:${view}:${scope || ""}:${handle}`;
    const cached = readCache(cacheKey);
    // (an entry cached by the pre-guard code may be a whole page: refetch it)
    if (typeof cached === "string" && (cached === "" || isFragment(cached))) return cached;
    try {
      const response = await fetchWithTimeout(`${routesRoot()}products/${encodeURIComponent(handle)}?view=${view}`);
      // A handle Rebuy still recommends but the storefront no longer sells
      // answers 404, and a URL redirect can turn that into a 301 to a
      // collection or the home page. fetch() follows it, so the "fragment"
      // would be a whole HTML document (header, stylesheets, sections);
      // appended to a slide it wrecks the PDP (half-zip-polo-light-gray ->
      // /collections/women-sweater on EU, 2026-09-18). Only a direct 200 of a
      // bare fragment is usable.
      let html = "";
      if (response.ok && !response.redirected) {
        html = await response.text();
        if (!isFragment(html)) html = "";
      }
      // A redirect / 404 / whole page is a definitive miss: cache the empty
      // answer so the session never downloads that page again. Anything else
      // (5xx, network) is transient and retried on the next call.
      if (response.ok || response.redirected || response.status === 404) writeCache(cacheKey, html);
      return html;
    } catch (e) {
      return "";
    }
  }

  /** A `layout none` render is bare markup; a whole page opens a document. */
  function isFragment(html) {
    return typeof html === "string" && html.trim() !== "" && !/<(!doctype|html|head|body)[\s>]/i.test(html);
  }

  async function fetchViews(handles, view, scope) {
    const results = await Promise.allSettled(handles.map(h => fetchProductView(h, view, scope)));
    return results.map(r => (r.status === "fulfilled" ? r.value : ""));
  }

  /**
   * The quick-cart drawer is one shared element rendered far away from the
   * card that opened it. While it was opened from a Rebuy-recommended card,
   * carry the host's attribution onto the drawer so the add-to-cart inside it
   * (ProductCard, global.js) can tag the line; any other opener clears it.
   */
  function markQuickCartOrigin(event) {
    const trigger = event.target && event.target.closest && event.target.closest(".quick-cart-drawer__trigger");
    if (!trigger) return;
    const drawer = document.querySelector("quick-cart-drawer");
    if (!drawer) return;
    const host = trigger.closest("[data-rebuy-attribution]");
    if (host) {
      drawer.dataset.rebuyAttribution = host.dataset.rebuyAttribution;
      if (host.dataset.rebuyWidgetId) drawer.dataset.rebuyWidgetId = host.dataset.rebuyWidgetId;
      else delete drawer.dataset.rebuyWidgetId;
    } else {
      delete drawer.dataset.rebuyAttribution;
      delete drawer.dataset.rebuyWidgetId;
    }
  }
  if (!window.__rebuyQuickCartOriginBound) {
    window.__rebuyQuickCartOriginBound = true;
    document.addEventListener("click", markQuickCartOrigin, true);
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // ---- PDP: <rebuy-recommendations> ----------------------------------------
  function defineRecommendations() {
    if (customElements.get("rebuy-recommendations")) return;
    const Base = customElements.get("card-product-slider");
    if (!Base) return;

    class RebuyRecommendations extends Base {
      constructor() {
        super();
        this.section = this.closest(".js-section");
        this.sliderWrapper = this.querySelector(".swiper-wrapper");
      }

      connectedCallback() {
        // Deliberately not calling super.connectedCallback(): the base defers
        // its boot until the element nears the viewport, but this section is
        // display:none until it has slides — it would never intersect.
        if (this.booted) return;
        this.booted = true;
        this.load();
      }

      async load() {
        const limit = parseInt(this.dataset.limit, 10) || 8;
        const productId = this.dataset.productId || "";
        const handles = await fetchRebuyHandles({
          key: this.dataset.apiKey,
          sourceId: this.dataset.sourceId,
          productIds: productId ? [productId] : [],
          limit,
          excludeIds: productId ? [productId] : []
        });

        if (handles.length) {
          // Rebuy answers a few more than `limit` (fetchRebuyHandles asks for
          // limit + exclusions), so a handle fetchProductView rejects still
          // leaves a full row.
          const cards = await fetchViews(handles, "card", this.dataset.cacheKey);
          let added = 0;
          cards.forEach(html => {
            if (!html || added >= limit) return;
            const slide = document.createElement("div");
            slide.classList.add("swiper-slide", "card-product-slider__slide");
            slide.insertAdjacentHTML("beforeend", html);
            if (!slide.querySelector(".product-card")) return;
            this.sliderWrapper.appendChild(slide);
            added += 1;
          });
          if (added) {
            this.reveal();
            return;
          }
        }
        this.fallback();
      }

      /** Shopify's own recommendations (the section rendered via data-url). */
      async fallback() {
        const url = this.getAttribute("data-url");
        if (!url) return;
        try {
          const response = await fetch(url);
          if (!response.ok) return;
          const html = new DOMParser().parseFromString(await response.text(), "text/html");
          const wrapper = html.querySelector(".swiper-wrapper");
          if (!wrapper || !wrapper.innerHTML.trim()) return;
          this.sliderWrapper.innerHTML = wrapper.innerHTML;
          // Not Rebuy-sourced any more — don't attribute these adds to Rebuy.
          delete this.dataset.rebuyAttribution;
          delete this.dataset.rebuyWidgetId;
          this.reveal();
        } catch (e) {
          /* leave the section hidden */
        }
      }

      reveal() {
        this.cleanRecommendationUrls();
        this.initSlider();
        this.syncHeights();
        window.addEventListener("resize", () => this.syncHeights());
        if (this.section) this.section.classList.remove("hidden");
      }
    }

    customElements.define("rebuy-recommendations", RebuyRecommendations);
  }

  if (customElements.get("card-product-slider")) {
    defineRecommendations();
  } else {
    customElements.whenDefined("card-product-slider").then(defineRecommendations);
  }

  // ---- Cart drawer: <rebuy-cart-recommendations> ----------------------------
  if (!customElements.get("rebuy-cart-recommendations")) {
    class RebuyCartRecommendations extends HTMLElement {
      connectedCallback() {
        // The drawer body is re-rendered (innerHTML) on every cart change, so a
        // fresh instance appears each time; the caches above keep that cheap.
        if (this.booted) return;
        this.booted = true;
        this.load();
      }

      async load() {
        const ids = (this.dataset.productIds || "").split(",").map(s => s.trim()).filter(Boolean);
        if (!ids.length) return;
        const limit = parseInt(this.dataset.limit, 10) || 4;
        const handles = await fetchRebuyHandles({
          key: this.dataset.apiKey,
          sourceId: this.dataset.sourceId,
          productIds: ids,
          limit,
          excludeIds: ids
        });
        if (!handles.length || !this.isConnected) return;

        const items = (await fetchViews(handles.slice(0, limit), "upsell", this.dataset.cacheKey)).filter(html => html && html.indexOf("product-recommendations__item") >= 0);
        if (!items.length || !this.isConnected) return;

        const scheme = this.dataset.colorScheme ? ` color-${this.dataset.colorScheme}` : "";
        this.innerHTML =
          `<product-recommendations class="product-recommendations cart-drawer__recommendations${escapeHtml(scheme)}" data-static-content="true" data-recommendation-area="cart-drawer">` +
          (this.dataset.title ? `<h3 class="h5 product-recommendations__title">${escapeHtml(this.dataset.title)}</h3>` : "") +
          `<div class="swiper product-recommendations__body"><div class="product-recommendations__items swiper-wrapper" data-recommendations>${items.join("")}</div></div>` +
          `</product-recommendations>`;

        // product-recommendations.js defers its own boot until the element
        // nears the viewport — but inside the drawer's scroll container
        // (overflow-y: auto) the element is clipped whenever it sits below the
        // fold, so that observer may never fire. We own the insertion point:
        // boot right away (the theme's pending observer then no-ops on
        // `booted`) and refresh Swiper's measurements once the element is
        // actually visible (it measures 0 widths while the drawer is closed).
        const rec = this.querySelector("product-recommendations");
        if (rec && !rec.booted && typeof rec.performRecommendations === "function") {
          rec.booted = true;
          rec.performRecommendations();
        }
        if (rec && typeof IntersectionObserver === "function") {
          const io = new IntersectionObserver(entries => {
            if (!entries.some(e => e.isIntersecting)) return;
            if (rec.slider && typeof rec.slider.update === "function") rec.slider.update();
            io.disconnect();
          });
          io.observe(rec);
        }
      }
    }

    customElements.define("rebuy-cart-recommendations", RebuyCartRecommendations);
  }
})();
